import React, { useState, useEffect } from 'react';
import { Upload, Save, RefreshCw } from 'lucide-react';
import api from '../services/api';
import { clearUploadConfigCache } from '../hooks/useUploadConfig';

function UploadConfigManager() {
  const [config, setConfig] = useState({
    max_photos: 6,
    max_file_size_mb: 2,
    enable_client_resize: true,
    client_resize_max_width: 1920,
    client_resize_max_height: 1080,
    client_resize_quality: 0.85,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.getUploadConfig();
      if (response.config) {
        setConfig({
          max_photos: response.config.maxPhotos ?? response.config.max_photos ?? 6,
          max_file_size_mb: response.config.maxFileSizeMB ?? response.config.max_file_size_mb ?? 2,
          enable_client_resize:
            response.config.enableClientResize ?? response.config.enable_client_resize ?? true,
          client_resize_max_width:
            response.config.clientResizeMaxWidth ?? response.config.client_resize_max_width ?? 1920,
          client_resize_max_height:
            response.config.clientResizeMaxHeight ??
            response.config.client_resize_max_height ??
            1080,
          client_resize_quality:
            response.config.clientResizeQuality ?? response.config.client_resize_quality ?? 0.85,
        });
      }
    } catch (err) {
      console.error('Error loading upload config:', err);
      setError('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await api.updateUploadConfig(config);
      clearUploadConfigCache(); // Clear cache so new config is fetched by frontend
      setSuccess('Configuration saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving upload config:', err);
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-500">Loading configuration...</span>
        </div>
      </div>
    );
  }

  const totalMaxUpload = config.max_photos * config.max_file_size_mb;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center">
          <Upload className="w-5 h-5 text-purple-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Photo Upload Configuration</h3>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Configure photo upload limits and automatic resizing for incident reports.
        </p>
      </div>

      <div className="p-6 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            {success}
          </div>
        )}

        {/* Basic Limits */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900 flex items-center">Upload Limits</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maximum Photos per Report
              </label>
              <input
                type="number"
                min="1"
                max="20"
                value={config.max_photos}
                onChange={e => handleChange('max_photos', parseInt(e.target.value) || 1)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Range: 1-20 photos</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maximum File Size (MB)
              </label>
              <input
                type="number"
                min="1"
                max="50"
                value={config.max_file_size_mb}
                onChange={e => handleChange('max_file_size_mb', parseInt(e.target.value) || 1)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">Range: 1-50 MB per file</p>
            </div>
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <p className="text-sm text-purple-800">
              <strong>Total Maximum Upload Size:</strong> {totalMaxUpload} MB
              <span className="text-purple-600 ml-1">
                ({config.max_photos} photos x {config.max_file_size_mb} MB each)
              </span>
            </p>
          </div>
        </div>

        {/* Client-Side Resizing */}
        <div className="space-y-4 pt-4 border-t border-gray-200">
          <h4 className="font-medium text-gray-900">Client-Side Image Resizing</h4>

          <div className="flex items-start">
            <input
              type="checkbox"
              id="enableResize"
              checked={config.enable_client_resize}
              onChange={e => handleChange('enable_client_resize', e.target.checked)}
              className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
            />
            <label htmlFor="enableResize" className="ml-3">
              <span className="text-sm font-medium text-gray-700">
                Enable automatic image resizing before upload
              </span>
              <p className="text-xs text-gray-500 mt-1">
                When enabled, large photos (especially from phones) will be automatically resized in
                the browser before uploading. This reduces upload time, saves bandwidth, and
                improves server performance.
              </p>
            </label>
          </div>

          {config.enable_client_resize && (
            <div className="ml-7 mt-4 space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Width (pixels)
                  </label>
                  <input
                    type="number"
                    min="100"
                    max="4000"
                    value={config.client_resize_max_width}
                    onChange={e =>
                      handleChange('client_resize_max_width', parseInt(e.target.value) || 1920)
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Range: 100-4000 px</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Height (pixels)
                  </label>
                  <input
                    type="number"
                    min="100"
                    max="4000"
                    value={config.client_resize_max_height}
                    onChange={e =>
                      handleChange('client_resize_max_height', parseInt(e.target.value) || 1080)
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Range: 100-4000 px</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  JPEG Quality: {Math.round(config.client_resize_quality * 100)}%
                </label>
                <input
                  type="range"
                  min="0.3"
                  max="1.0"
                  step="0.05"
                  value={config.client_resize_quality}
                  onChange={e => handleChange('client_resize_quality', parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Lower quality (smaller files)</span>
                  <span>Higher quality (larger files)</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Recommended: 85% for good balance of quality and file size.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="pt-4 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Configuration
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UploadConfigManager;
